/* eslint-disable no-param-reassign */
/* eslint-disable react/prop-types */
/* eslint-disable react/no-unstable-nested-components */

// @mui material components

// prop-types is library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MaterialReactTable from "material-react-table";

import { useEffect, useMemo, useState } from "react";

function YearEndInvoiceSprayList({ title, invoiceData, shadow }) {
  const [spraysWithCalc, setSpraysWithCalc] = useState([]);
  const [rowSelection, setRowSelection] = useState({});
  // Preprocess sprays
  useEffect(() => {
    if (invoiceData?.sprays?.length > 0) {
      const mapped = invoiceData.sprays.map((spray) => {
        const chemicalcost = spray.chemicalcost ?? 0;
        const tenPercent = chemicalcost * 0.1;
        const billableCost = chemicalcost * 1.1;
        return { ...spray, tenPercent, billableCost };
      });
      setSpraysWithCalc(mapped);
    } else {
      setSpraysWithCalc([]);
    }
  }, [invoiceData]);

  const totalCost = useMemo(
    () => spraysWithCalc.reduce((acc, curr) => acc + (curr.billableCost ?? 0), 0),
    [spraysWithCalc]
  );

  const columns = useMemo(
    () => [
      {
        header: "Orchard",
        accessorKey: "orchard",
        Cell: ({ cell }) => <>{cell.getValue()}</>,
      },
      {
        accessorKey: "date",
        header: "Spray Date",
      },
      {
        accessorKey: "chemicalName",
        header: "Chemical",
      },
      {
        accessorKey: "totalused",
        header: "Total Used",
      },
      {
        accessorKey: "cost",
        header: "Cost",
      },
      {
        accessorKey: "chemicalcost",
        id: "chemcost",
        header: "Chem. Cost",
        Cell: ({ cell }) => (
          // eslint-disable-next-line react/jsx-no-useless-fragment
          <>{`$${cell.getValue().toFixed(2)}`}</>
        ),
      },
      {
        accessorKey: "tenPercent",
        id: "tenpercent",
        header: "10%",
        Cell: ({ cell }) => (
          // eslint-disable-next-line react/jsx-no-useless-fragment
          <>{`$${cell.getValue().toFixed(2)}`}</>
        ),
      },
      {
        accessorKey: "billableCost",
        header: "Billable Cost",
        aggregationFn: "sum",
        AggregatedCell: ({ cell, table }) => (
          <>
            Total Cost - {table.getColumn(cell.row.groupingColumnId ?? "").columnDef.header}:{" "}
            <MDBox sx={{ color: "info.main", display: "inline", fontWeight: "bold" }}>
              {cell.getValue()?.toLocaleString?.("en-US", {
                style: "currency",
                currency: "USD",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </MDBox>
          </>
        ),
        Cell: ({ cell }) =>
          cell.getValue()?.toLocaleString?.("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),
        Footer: () => (
          <Stack>
            Invoice Total:
            <MDBox color="warning.main">
              {totalCost.toLocaleString?.("en-US", {
                style: "currency",
                currency: "USD",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </MDBox>
          </Stack>
        ),
      },
    ],
    [spraysWithCalc, totalCost]
  );

  return (
    <Card sx={{ height: "100%", boxShadow: !shadow && "none" }}>
      <MDBox pt={4} px={2}>
        <MDTypography variant="h4" fontWeight="medium" textTransform="capitalize">
          {title}
        </MDTypography>
      </MDBox>
      <MDBox pt={4} px={2}>
        <MaterialReactTable
          columns={columns}
          data={spraysWithCalc}
          enableGrouping
          enableStickyHeader
          enableStickyFooter
          enableSelectAll={false}
          enableColumnActions={false}
          enablePagination={false}
          initialState={{
            density: "compact",
            expanded: false,
            grouping: ["orchard", "chemicalName"],
            pagination: { pageIndex: 0, pageSize: 20 },
            sorting: [{ id: "orchard", desc: false }],
            tableLayout: "fixed",
          }}
          muiToolbarAlertBannerChipProps={{ color: "primary" }}
          muiTableContainerProps={{ sx: { maxHeight: 700 } }}
          muiTableHeadCellProps={{
            // no useTheme hook needed, just use the `sx` prop with the theme callback
            sx: () => ({
              backgroundColor: "lightblue",
            }),
          }}
          muiTableBodyRowProps={({ row }) => ({
            onClick: row.getToggleSelectedHandler(),
            hover: false,
            sx: () => ({
              backgroundColor: row.original.color,
              cursor: "pointer",
            }),
          })}
          onRowSelectionChange={setRowSelection}
          state={{ rowSelection }}
          muiSelectCheckboxProps={({ row }) => ({
            color: row.original.senttoProlorus === true ? "white" : "secondary",
          })}
        />
      </MDBox>
    </Card>
  );
}

// Setting default props for the ProfilesList
YearEndInvoiceSprayList.defaultProps = {
  shadow: true,
};

// Typechecking props for the ProfilesList
YearEndInvoiceSprayList.propTypes = {
  title: PropTypes.string.isRequired,
  shadow: PropTypes.bool,
  invoiceData: PropTypes.objectOf.isRequired,
};

export default YearEndInvoiceSprayList;
